import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  useConversationAssigneeGroupsManagement,
  useCreateConversationAssigneeGroup,
  useUpdateConversationAssigneeGroup,
} from 'src/graphql/entities/conversation-assignee-group';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import {
  AssigneeGroupFormField,
  type AssigneeGroupFormValues,
  getAssigneeGroupDefaults,
  useAssigneeGroupForm,
} from './use-assignee-group-form';

export const ManageAssigneeGroupModal = () => {
  const { closeModal } = useModalMethods(Modals.ManageAssigneeGroup);
  const { isOpened, modalProps } = useModalState(Modals.ManageAssigneeGroup);

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const partnerId = modalProps?.partnerId ?? null;
  const mode = modalProps?.mode;

  const editingGroup = modalProps?.mode === 'edit' ? modalProps.group : undefined;

  const { groups: existingGroups, cacheManager } =
    useConversationAssigneeGroupsManagement(partnerId);
  const { createConversationAssigneeGroup, loading: creating } =
    useCreateConversationAssigneeGroup(cacheManager);
  const { updateConversationAssigneeGroup, loading: updating } =
    useUpdateConversationAssigneeGroup();

  const formMethods = useForm<AssigneeGroupFormValues>({
    defaultValues: getAssigneeGroupDefaults(editingGroup),
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpened) return;
    formMethods.reset(getAssigneeGroupDefaults(editingGroup));
  }, [isOpened, editingGroup, formMethods]);

  const formFields = useAssigneeGroupForm({
    formMethods,
    partnerId,
    existingGroups: existingGroups ?? [],
    editingId: editingGroup?.id,
  });

  const onSubmit = formMethods.handleSubmit(async (values) => {
    if (partnerId === null) return;

    const trimmedName = values[AssigneeGroupFormField.Name].trim();

    try {
      if (mode === 'edit' && editingGroup) {
        await updateConversationAssigneeGroup(
          {
            id: editingGroup.id,
            name: trimmedName,
            description: values[AssigneeGroupFormField.Description],
            icon: values[AssigneeGroupFormField.Icon],
          },
          editingGroup,
        );
        showSuccessAlert('Group updated');
      } else {
        await createConversationAssigneeGroup({
          partnerId,
          name: trimmedName,
          description: values[AssigneeGroupFormField.Description] || undefined,
          icon: values[AssigneeGroupFormField.Icon] || undefined,
        });
        showSuccessAlert('Group created');
      }
      closeModal();
    } catch (err) {
      showErrorAlert(
        err instanceof Error ? err.message : 'Failed to save group. Please try again.',
      );
    }
  });

  const isMutating = creating || updating;
  const isEdit = mode === 'edit';

  return (
    <ModalContainer
      open={Boolean(isOpened)}
      size="medium"
      title={isEdit ? 'Edit assignee group' : 'Add assignee group'}
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'manage-assignee-group-cancel' },
        {
          label: isEdit ? 'Save changes' : 'Create group',
          kind: 'primary',
          onClick: () => void onSubmit(),
          disabled: !formMethods.formState.isValid,
          loading: isMutating,
          loadingLabel: isEdit ? 'Saving…' : 'Creating group…',
          testId: 'manage-assignee-group-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="manage-assignee-group-modal"
    >
      <GenericForm<AssigneeGroupFormValues> formMethods={formMethods}>
        {formFields}
      </GenericForm>
    </ModalContainer>
  );
};
